import { useState, useEffect } from 'react'
import { UserProfile, useUser } from '@clerk/clerk-react'
import { PageHeader, Card, CardHeader, Input, Button, Checkbox } from '@orientamos/ui'
import { Save, Check } from 'lucide-react'

// Default settings stored in localStorage
interface UserSettings {
  defaultCommissionPercentage: number
  defaultMonthlyFee: number
  notifyOnContractGenerated: boolean
  notifyOnContractSigned: boolean
  weeklyDigest: boolean
}

const DEFAULT_SETTINGS: UserSettings = {
  defaultCommissionPercentage: 10,
  defaultMonthlyFee: 0,
  notifyOnContractGenerated: true,
  notifyOnContractSigned: true,
  weeklyDigest: false,
}

const STORAGE_KEY = 'orientamos_settings'

export default function SettingsPage() {
  const { user } = useUser()
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS)
  const [saved, setSaved] = useState(false)
  const [showClerkProfile, setShowClerkProfile] = useState(false)

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        setSettings({ ...DEFAULT_SETTINGS, ...parsed })
      }
    } catch (e) {
      console.error('Error loading settings:', e)
    }
  }, [])

  const handleSave = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      console.error('Error saving settings:', e)
    }
  }

  const updateSetting = <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div>
      <PageHeader
        title="Ajustes"
        description="Configura las preferencias de la aplicacion"
        breadcrumbs={[{ label: 'Inicio', href: '/' }, { label: 'Ajustes' }]}
      />

      <div className="space-y-6">
        {/* User Profile Section */}
        <Card>
          <CardHeader
            title="Perfil de Usuario"
            description="Informacion de tu cuenta"
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowClerkProfile(!showClerkProfile)}
              >
                {showClerkProfile ? 'Ocultar' : 'Editar Perfil'}
              </Button>
            }
          />
          <div className="mt-6">
            {showClerkProfile ? (
              <div className="flex justify-center">
                <UserProfile
                  appearance={{
                    elements: {
                      rootBox: 'w-full max-w-2xl',
                      card: 'shadow-none border border-neutral-200',
                    },
                  }}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Nombre" value={user?.firstName || ''} disabled />
                <Input label="Apellido" value={user?.lastName || ''} disabled />
                <Input
                  label="Correo electronico"
                  value={user?.primaryEmailAddress?.emailAddress || ''}
                  disabled
                />
                <Input
                  label="Telefono"
                  value={user?.primaryPhoneNumber?.phoneNumber || 'No configurado'}
                  disabled
                />
              </div>
            )}
          </div>
        </Card>

        {/* Commission Defaults */}
        <Card>
          <CardHeader
            title="Valores por Defecto"
            description="Valores predeterminados para nuevas ordenes de contrato"
          />
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Porcentaje de Comision (%)"
              type="number"
              min={0}
              max={100}
              value={settings.defaultCommissionPercentage}
              onChange={(e) => updateSetting('defaultCommissionPercentage', Number(e.target.value))}
              hint="Porcentaje de comision por defecto para nuevas ordenes"
            />
            <Input
              label="Cuota Mensual por Defecto (COP)"
              type="number"
              min={0}
              value={settings.defaultMonthlyFee}
              onChange={(e) => updateSetting('defaultMonthlyFee', Number(e.target.value))}
              hint="Cuota mensual de administracion por defecto"
            />
          </div>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader
            title="Notificaciones"
            description="Configura como recibir alertas y notificaciones"
          />
          <div className="mt-6 space-y-4">
            <Checkbox
              label="Contrato generado"
              description="Recibe una notificacion cuando se genere un nuevo contrato"
              checked={settings.notifyOnContractGenerated}
              onChange={(e) => updateSetting('notifyOnContractGenerated', e.target.checked)}
            />
            <Checkbox
              label="Contrato firmado"
              description="Recibe una notificacion cuando un contrato sea firmado"
              checked={settings.notifyOnContractSigned}
              onChange={(e) => updateSetting('notifyOnContractSigned', e.target.checked)}
            />
            <Checkbox
              label="Resumen semanal"
              description="Recibe un resumen de actividad cada semana"
              checked={settings.weeklyDigest}
              onChange={(e) => updateSetting('weeklyDigest', e.target.checked)}
            />
          </div>
        </Card>

        {/* Company Information (read-only) */}
        <Card>
          <CardHeader
            title="Informacion de la Empresa"
            description="Datos que apareceran en los contratos generados"
          />
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nombre de la Empresa" defaultValue="Orientamos Propiedad Raiz" disabled />
            <Input label="NIT" defaultValue="900.123.456-7" disabled />
            <Input label="Direccion" defaultValue="Calle 10 #43-12, Medellin" disabled />
            <Input label="Telefono" defaultValue="+57 (4) 444-5678" disabled />
          </div>
          <p className="mt-4 text-sm text-neutral-500">
            Para actualizar la informacion de la empresa, contacta al administrador del sistema.
          </p>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          {saved && (
            <div className="flex items-center gap-2 text-sm text-secondary-600">
              <Check className="w-4 h-4" />
              Cambios guardados
            </div>
          )}
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            Guardar Cambios
          </Button>
        </div>
      </div>
    </div>
  )
}
